import React, { useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import { COMPANY } from "../data/site";

const CONTACT_API =
    process.env.REACT_APP_CONTACT_API_URL || "/api/send-enquiry.php";

const EMPTY_FORM = {
    name: "",
    company: "",
    phone: "",
    email: "",
    requirement: "",
    city: "",
    message: "",
};

export const LeadForm = ({ compact = false }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const buildWhatsAppMessage = () =>
        `New Enquiry — VIL Engineering%0A%0AName: ${form.name}%0ACompany: ${form.company}%0APhone: ${form.phone}%0AEmail: ${form.email}%0ARequirement: ${form.requirement}%0ACity: ${form.city}%0AMessage: ${form.message}`;

    const sendWhatsApp = (e) => {
        e.preventDefault();
        if (!form.name || !form.phone) {
            toast.error("Please share your name and phone number.");
            return;
        }
        window.open(
            `https://wa.me/${COMPANY.whatsapp}?text=${buildWhatsAppMessage()}`,
            "_blank"
        );
        toast.success("Opening WhatsApp with your enquiry...");
    };

    const sendEmail = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Please share your name and email.");
            return;
        }
        if (!form.phone) {
            toast.error("Please share your phone number.");
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(CONTACT_API, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });

            const raw = await response.text();
            let data = {};
            try {
                data = raw ? JSON.parse(raw) : {};
            } catch {
                throw new Error(
                    "Contact server is not responding correctly. Ensure api/send-enquiry.php is uploaded and PHP mail/SMTP is configured."
                );
            }

            if (!response.ok || !data.success) {
                throw new Error(data.message || "Failed to send enquiry.");
            }

            toast.success("Enquiry sent! We'll respond within 24 hours.");
            setForm(EMPTY_FORM);
        } catch (error) {
            const isNetworkError = error.name === "TypeError";
            toast.error(
                isNetworkError
                    ? "Could not reach the contact server. Check your connection or use WhatsApp."
                    : error.message ||
                          "Could not send your enquiry. Please try WhatsApp or call us directly."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            className={`glass corner-accent relative p-6 md:p-8 ${compact ? "" : ""}`}
            onSubmit={sendEmail}
            data-testid="lead-form"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full Name *" value={form.name} onChange={update("name")} testid="lead-name" />
                <Field label="Company" value={form.company} onChange={update("company")} testid="lead-company" />
                <Field label="Phone *" type="tel" value={form.phone} onChange={update("phone")} testid="lead-phone" />
                <Field label="Email *" type="email" value={form.email} onChange={update("email")} testid="lead-email" />
                <Field
                    label="Requirement"
                    value={form.requirement}
                    onChange={update("requirement")}
                    placeholder="e.g. Sprinkler installation for warehouse"
                    testid="lead-requirement"
                />
                <Field
                    label="City"
                    value={form.city}
                    onChange={update("city")}
                    placeholder="Palghar / Mumbai / Vasai"
                    testid="lead-city"
                />
            </div>
            <label className="block mt-4">
                <span className="eyebrow block mb-2">Message</span>
                <textarea
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your project, site location, and timeline..."
                    className="w-full bg-slate-50 border border-slate-200 text-slate-900 px-4 py-3 rounded-sm focus:border-[#E53935] focus:outline-none focus:ring-1 focus:ring-[#E53935] focus:bg-white transition placeholder:text-slate-400"
                    data-testid="lead-message"
                />
            </label>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                    type="submit"
                    className="btn-primary flex-1 justify-center"
                    data-testid="lead-submit-email"
                    disabled={submitting}
                >
                    {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Send className="w-4 h-4" />
                    )}
                    {submitting ? "Sending..." : "Send Enquiry"}
                </button>
                <button
                    type="button"
                    onClick={sendWhatsApp}
                    className="btn-secondary flex-1 justify-center"
                    style={{ borderColor: "#25D366", color: "#25D366" }}
                    data-testid="lead-submit-whatsapp"
                >
                    <MessageCircle className="w-4 h-4" />
                    Send via WhatsApp
                </button>
            </div>
            <p className="text-xs text-slate-500 mt-4 font-mono">
                * Required · We respond to industrial enquiries within 24 hours.
            </p>
        </form>
    );
};

const Field = ({ label, value, onChange, type = "text", placeholder = "", testid }) => (
    <label className="block">
        <span className="eyebrow block mb-2">{label}</span>
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            data-testid={testid}
            className="w-full bg-slate-50 border border-slate-200 text-slate-900 px-4 py-3 rounded-sm focus:border-[#E53935] focus:outline-none focus:ring-1 focus:ring-[#E53935] focus:bg-white transition placeholder:text-slate-400"
        />
    </label>
);

export default LeadForm;
